import React from 'react';
import { Check, ArrowRight, Shield } from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/swallacemeeting/bizdevconsult';

const plans = [
  {
    title: 'Outbound as a Service',
    subtitle: 'We run it for you',
    price: '$5,000',
    period: '/month',
    description: 'We run your complete outbound system — cold calling, cold email, lead follow-up — so you can focus on closing.',
    features: [
      'Cold calling campaigns',
      'Cold email sequences',
      'Lead qualification & nurturing',
      'CRM management',
      'Monthly reporting & optimization',
    ],
    addOns: [
      { label: 'LinkedIn Strategy & Outreach', price: '+$2,000/mo', detail: 'Tap into the platform behind 80%+ of B2B social selling deals' },
    ],
    guarantee: 'Triple your pipeline in 6 months or we work for free until you do.',
    popular: false,
    pricingNote: null,
  },
  {
    title: 'Outbound Infrastructure',
    subtitle: 'We build it, you run it',
    price: '$7,500',
    period: ' one-time',
    description: 'Everything your team needs to run outbound themselves — scripts, templates, coaching, and tools. Built by pros.',
    features: [
      'Custom script development',
      'Email template creation',
      'Target list building',
      'Dialer setup & configuration',
      'Team training & coaching',
      'Ongoing support available',
    ],
    guarantee: null,
    popular: true,
    pricingNote: null,
  },
  {
    title: 'NIMBLE Pipeline System',
    subtitle: 'Referral generation engine',
    price: '$2,500',
    period: '/mo starting',
    description: 'A self-perpetuating referral system. Stop buying leads, cold calling, and networking aimlessly.',
    features: null,
    guarantee: null,
    popular: false,
    pricingNote: [
      { label: 'Fixed Rate', detail: '$2,500/mo flat' },
      { label: 'Incremental', detail: 'Start lower, increase a set amount each month' },
      { label: 'Progressive', detail: 'Start lower, increase at revenue milestones' },
      { label: 'Performance', detail: 'Lower base + revenue share on closed business' },
    ],
  },
];

const Pricing = () => {
  return (
    <section id="pricing" data-testid="pricing-section" className="section" style={{ backgroundColor: 'var(--bg-secondary)' }}>
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="heading-lg mb-4">Simple Pricing. No Surprises.</h2>
          <p className="text-base md:text-lg" style={{ color: 'var(--text-secondary)' }}>
            Pick the plan that fits. Let's get to work.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {plans.map((plan) => (
            <div
              key={plan.title}
              data-testid={`pricing-card-${plan.title.toLowerCase().replace(/\s+/g, '-')}`}
              className={`card relative ${plan.popular ? 'border-2 lg:scale-105' : ''}`}
              style={{ borderColor: plan.popular ? 'var(--brand-primary)' : undefined }}
            >
              {plan.popular && (
                <div
                  className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white"
                  style={{ backgroundColor: 'var(--brand-primary)' }}
                >
                  MOST POPULAR
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-xl font-bold mb-1">{plan.title}</h3>
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{plan.subtitle}</p>
              </div>

              <div className="mb-6">
                <span className="text-4xl font-extrabold" style={{ color: 'var(--text-primary)' }}>
                  {plan.price}
                </span>
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>{plan.period}</span>
              </div>

              <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                {plan.description}
              </p>

              {/* Standard features list */}
              {plan.features && (
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: 'var(--brand-success)' }} />
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{feature}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* A la carte add-ons */}
              {plan.addOns && (
                <div className="mb-6 p-4 rounded-xl border border-dashed border-gray-200" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                  <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
                    A La Carte
                  </p>
                  {plan.addOns.map((addon) => (
                    <div key={addon.label} className="flex items-start gap-3">
                      <span className="text-sm font-bold flex-shrink-0" style={{ color: 'var(--brand-primary)' }}>{addon.price}</span>
                      <div>
                        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{addon.label}</span>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{addon.detail}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* NIMBLE pricing models */}
              {plan.pricingNote && (
                <div className="mb-6">
                  <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
                    4 Ways to Work Together
                  </p>
                  <div className="space-y-3">
                    {plan.pricingNote.map((model) => (
                      <div key={model.label} className="flex items-start gap-3">
                        <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: 'var(--brand-success)' }} />
                        <div>
                          <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{model.label}: </span>
                          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{model.detail}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Guarantee callout */}
              {plan.guarantee && (
                <div
                  className="flex items-start gap-2 p-3 rounded-xl mb-6 text-sm"
                  style={{ backgroundColor: '#ecfdf5', color: 'var(--brand-success)' }}
                >
                  <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span className="font-medium">{plan.guarantee}</span>
                </div>
              )}

              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={plan.popular ? 'btn-primary w-full' : 'btn-secondary w-full'}
                data-testid={`pricing-cta-${plan.title.toLowerCase().replace(/\s+/g, '-')}`}
                aria-label={`Book a call about ${plan.title}`}
              >
                Book a Call
                <ArrowRight className="ml-2 w-4 h-4" />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
